import { useState, useCallback, useEffect } from 'react';
import { NativeChessBoard } from './NativeChessBoard';
import { SavedGamesList } from './SavedGamesList';
import { EngineStatusPanel } from './EngineStatusPanel';
import { TeenFriendlyAnalysisPanel } from './TeenFriendlyAnalysisPanel';
import { EnhancedSaveGameSystem } from './EnhancedSaveGameSystem';
import { MultiLineAnalysis } from './MultiLineAnalysis';
import { MoveHistory } from './MoveHistory';
import { ChessComGameReview } from './ChessComGameReview';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { createInitialGameState, validateMove, type GameState, type Position, type PieceType } from '@/lib/chess-engine';
import { gameStateToFen, fenToGameState } from '@/lib/fen-parser';
import { gameStatesToPgn, pgnToGameStates } from '@/lib/pgn';
import { ensureStockfishReady } from '@/lib/stockfish-engine';
import { uciToSan } from '@/lib/move-notation';
import { useCosmetics } from '@/hooks/useCosmetics';
import type { PieceStyle } from '@/lib/pieces';
import { 
  Upload, Download, RotateCcw, SkipBack, SkipForward, 
  ChevronLeft, ChevronRight 
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Move {
  from: Position;
  to: Position;
  promotion?: PieceType;
}

interface AnalysisState {
  analysis: any;
  isAnalyzing: boolean;
  error: string | null;
  hasAttemptedAnalysis: boolean;
  currentDepth?: number;
}

interface AnalysisBoardProps {
  analysisState: AnalysisState;
  analysisEnabled: boolean;
  onAnalysisEnabledChange: (enabled: boolean) => void;
}

export function AnalysisBoard({ analysisState, analysisEnabled, onAnalysisEnabledChange }: AnalysisBoardProps) {
  const [gameHistory, setGameHistory] = useState<GameState[]>([createInitialGameState()]);
  const [currentMoveIndex, setCurrentMoveIndex] = useState(0);
  const [selectedSquare, setSelectedSquare] = useState<Position | null>(null);
  const [orientation, setOrientation] = useState<'white' | 'black'>('white');
  const [showSavedGames, setShowSavedGames] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importText, setImportText] = useState('');
  
  const { toast } = useToast();
  const { boardTheme, pieceStyle } = useCosmetics();
  const currentGameState = gameHistory[currentMoveIndex];

  // Helper function to find the move between two game states
  const findMoveBetweenStates = (fromState: GameState, toState: GameState): string | null => {
    // Compare boards to find the move
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const fromPiece = fromState.board[row][col];
        const toPiece = toState.board[row][col];
        
        // Found a piece that moved
        if (fromPiece && !toPiece) {
          // Look for where this piece went
          for (let toRow = 0; toRow < 8; toRow++) {
            for (let toCol = 0; toCol < 8; toCol++) {
              const destPiece = toState.board[toRow][toCol];
              const origDestPiece = fromState.board[toRow][toCol];
              
              // Found where the piece moved to
              if (destPiece && destPiece.type === fromPiece.type && 
                  destPiece.color === fromPiece.color && 
                  (!origDestPiece || origDestPiece.type !== destPiece.type || origDestPiece.color !== destPiece.color)) {
                
                const fromSquare = String.fromCharCode(97 + col) + (8 - row);
                const toSquare = String.fromCharCode(97 + toCol) + (8 - toRow);
                return fromSquare + toSquare;
              }
            }
          }
        }
      }
    }
    return null;
  };

  // Warm up Stockfish when component mounts
  useEffect(() => {
    const warmupEngine = async () => {
      try {
        await ensureStockfishReady();
        console.log('[Analysis] Engine warmed up successfully');
      } catch (error) {
        console.warn('[Analysis] Engine warmup failed:', error);
      }
    };
    
    warmupEngine();
  }, []);

  const handleMove = useCallback((move: Move) => {
    const moveResult = validateMove(currentGameState, move.from, move.to, move.promotion);
    if (moveResult.isLegal && moveResult.newState) {
      const newHistory = gameHistory.slice(0, currentMoveIndex + 1);
      newHistory.push(moveResult.newState);
      setGameHistory(newHistory);
      setCurrentMoveIndex(newHistory.length - 1);
    }
  }, [currentGameState, gameHistory, currentMoveIndex]);

  const handleBoardClick = useCallback((position: Position) => {
    if (selectedSquare) {
      if (selectedSquare[0] === position[0] && selectedSquare[1] === position[1]) {
        setSelectedSquare(null);
      } else {
        handleMove({ from: selectedSquare, to: position });
        setSelectedSquare(null);
      }
    } else {
      setSelectedSquare(position);
    }
  }, [selectedSquare, handleMove]);

  const goToMove = (moveIndex: number) => {
    setCurrentMoveIndex(Math.max(0, Math.min(moveIndex, gameHistory.length - 1)));
    setSelectedSquare(null);
  };

  const handleImportPgn = useCallback(() => {
    if (!importText.trim()) return;
    
    try {
      const newGameStates = pgnToGameStates(importText);
      setGameHistory(newGameStates);
      setCurrentMoveIndex(newGameStates.length - 1);
      toast({
        title: "Success",
        description: "PGN imported successfully",
      });
      setImportText('');
      setShowImportDialog(false);
    } catch (error) {
      toast({
        title: "Import Failed",
        description: error instanceof Error ? error.message : 'Invalid PGN format',
        variant: "destructive",
      });
    }
  }, [importText, toast]);

  const handleImportFen = useCallback(() => {
    if (!importText.trim()) return;
    
    try {
      const gameState = fenToGameState(importText);
      setGameHistory([gameState]);
      setCurrentMoveIndex(0);
      toast({
        title: "Success",
        description: "FEN imported successfully",
      });
      setImportText('');
      setShowImportDialog(false);
    } catch (error) {
      toast({
        title: "Import Failed",
        description: error instanceof Error ? error.message : 'Invalid FEN format',
        variant: "destructive",
      });
    }
  }, [importText, toast]);

  const handleLoadSavedGame = (savedGame: any) => {
    try {
      // Load the game state and moves
      if (savedGame.gameState && savedGame.moves) {
        // Set the game history from the saved game
        const newHistory = [createInitialGameState()];
        let currentState = createInitialGameState();
        
        // Replay all moves to rebuild the game history
        savedGame.moves.forEach((moveNotation: string) => {
          // This would need proper move parsing - for now just set the final state
          currentState = savedGame.gameState;
          newHistory.push({ ...currentState });
        });
        
        setGameHistory(newHistory);
        setCurrentMoveIndex(newHistory.length - 1);
        setShowSavedGames(false);
        toast({
          title: "Success",
          description: `Loaded game vs ${savedGame.opponent}`,
        });
      }
    } catch (error) {
      console.error('Error loading saved game:', error);
      toast({
        title: "Error",
        description: "Failed to load saved game",
        variant: "destructive",
      });
    }
  };

  const handleExportPgn = () => {
    const pgn = gameStatesToPgn(gameHistory);
    const blob = new Blob([pgn], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'game.pgn';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="h-screen flex bg-background">
      {/* Left Panel - Board */}
      <div className="flex-1 flex flex-col">
        {/* Game Status Bar */}
        <div className="bg-card border-b p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSavedGames(!showSavedGames)}
              >
                {showSavedGames ? 'Hide' : 'Show'} Saved Games
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowImportDialog(true)}
              >
                <Upload className="h-4 w-4 mr-2" />
                Import
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportPgn}
                disabled={gameHistory.length === 0}
              >
                <Download className="h-4 w-4 mr-2" />
                Export PGN
              </Button>
            </div>
            
            <div className="flex items-center space-x-2">
              <Switch
                checked={analysisEnabled}
                onCheckedChange={onAnalysisEnabledChange}
                id="analysis-toggle"
              />
              <Label htmlFor="analysis-toggle" className="text-sm">
                Live Analysis
              </Label>
            </div>
          </div>
        </div>

        {/* Saved Games Panel */}
        {showSavedGames && (
          <div className="bg-card border-b p-4">
            <SavedGamesList onLoadGame={handleLoadSavedGame} />
          </div>
        )}

        {/* Board Container */}
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl aspect-square">
            <NativeChessBoard
              gameState={currentGameState}
              selectedSquare={selectedSquare}
              onSquareClick={handleBoardClick}
              flipped={orientation === 'black'}
              boardTheme={boardTheme}
              pieceStyle={pieceStyle as PieceStyle}
            />
          </div>
        </div>

        {/* Game Controls */}
        <div className="bg-card border-t p-4">
          <div className="flex items-center justify-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToMove(0)}
              disabled={currentMoveIndex === 0}
            >
              <SkipBack className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToMove(Math.max(0, currentMoveIndex - 1))}
              disabled={currentMoveIndex === 0}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToMove(Math.min(gameHistory.length, currentMoveIndex + 1))}
              disabled={currentMoveIndex === gameHistory.length}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToMove(gameHistory.length)}
              disabled={currentMoveIndex === gameHistory.length}
            >
              <SkipForward className="h-4 w-4" />
            </Button>
            <div className="mx-4 text-sm text-muted-foreground">
              Move {currentMoveIndex} of {gameHistory.length}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOrientation(prev => prev === 'white' ? 'black' : 'white')}
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Right Panel - Teen-Friendly Analysis */}
      <div className="w-full lg:w-80 xl:w-96 space-y-4 p-4 bg-card border-l overflow-y-auto">
        {/* Top Priority: Teen-Friendly Analysis Panel with Enhanced Evaluation Bar */}
        <TeenFriendlyAnalysisPanel
          analysis={analysisState.analysis}
          isAnalyzing={analysisState.isAnalyzing}
          hasAttemptedAnalysis={analysisState.hasAttemptedAnalysis}
          currentDepth={analysisState.currentDepth}
          onExportPgn={handleExportPgn}
        />

        {/* Move History - High Priority for Navigation */}
        <MoveHistory
          gameStates={gameHistory}
          currentMoveIndex={currentMoveIndex}
          onMoveClick={goToMove}
          gameSessionId={undefined}
        />

        {/* Multi-Line Analysis - Engine Variations */}
        <MultiLineAnalysis
          analysis={analysisState.analysis}
          isAnalyzing={analysisState.isAnalyzing}
        />

        {/* Engine Status - Compact version */}
        <EngineStatusPanel
          isAnalyzing={analysisState.isAnalyzing}
          error={analysisState.error}
          engineStatus={{
            isReady: !analysisState.isAnalyzing,
            isAnalyzing: analysisState.isAnalyzing,
            error: analysisState.error,
            responseTime: 0,
            source: 'local'
          }}
          onEngineReset={async () => {}}
        />

        {/* Enhanced Save System */}
        <EnhancedSaveGameSystem
          gameState={{
            position: gameStateToFen(currentGameState),
            moves: gameHistory.slice(1).map((gameState, index) => {
              const prevState = gameHistory[index];
              const move = findMoveBetweenStates(prevState, gameState);
              if (move) {
                try {
                  return uciToSan(prevState, move);
                } catch {
                  return move;
                }
              }
              return `Move ${index + 1}`;
            }),
            currentMoveIndex
          }}
          analysis={analysisState.analysis}
        />

        {/* Chess.com Style Game Review - Bottom */}
        <ChessComGameReview 
          gameStates={gameHistory}
          onAnalysisStart={() => onAnalysisEnabledChange(false)}
          onAnalysisFinish={() => onAnalysisEnabledChange(true)}
        />
      </div>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Game</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="pgn" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="pgn">PGN</TabsTrigger>
              <TabsTrigger value="fen">FEN</TabsTrigger>
            </TabsList>
            <TabsContent value="pgn" className="space-y-4">
              <Textarea
                placeholder="Paste PGN here..."
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                rows={8}
              />
              <Button onClick={handleImportPgn} className="w-full">
                Import PGN
              </Button>
            </TabsContent>
            <TabsContent value="fen" className="space-y-4">
              <Input
                placeholder="Paste FEN here..."
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
              />
              <Button onClick={handleImportFen} className="w-full">
                Import FEN
              </Button>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}